import React, { useState } from 'react';
import {
  useTherapistSessions,
  useTherapistStats,
  useDownloadReceipt,
  useSessionDetails,
  SessionHistoryFilters,
} from '@theraply/api-client';
import {
  SessionCard,
  SessionFilters,
  SessionDetails,
  ReceiptPreview,
} from '@theraply/ui';

export const SessionHistoryPage: React.FC = () => {
  const [filters, setFilters] = useState<SessionHistoryFilters>({});
  const [selectedSessionId, setSelectedSessionId] = useState<number | undefined>();
  const [showReceipt, setShowReceipt] = useState(false);

  // Fetch session history
  const { data: sessionData, isLoading, error } = useTherapistSessions(filters);
  const { data: stats } = useTherapistStats();
  const { data: selectedSession } = useSessionDetails(selectedSessionId);
  const downloadReceipt = useDownloadReceipt();

  const handleViewDetails = (sessionId: number) => {
    setSelectedSessionId(sessionId);
  };

  const handleCloseDetails = () => {
    setSelectedSessionId(undefined);
  };

  const handleDownloadReceipt = (sessionId: number) => {
    downloadReceipt.mutate(sessionId);
  };

  const handleViewReceipt = (sessionId: number) => {
    setSelectedSessionId(sessionId);
    setShowReceipt(true);
  };

  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setSelectedSessionId(undefined);
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading session history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h3 className="text-red-800 font-semibold mb-2">Error Loading Sessions</h3>
          <p className="text-red-600 text-sm">
            Failed to load your session history. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Session History</h1>
          <p className="text-gray-600 mt-2">
            View your completed and upcoming therapy sessions
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Total Sessions
              </h3>
              <p className="text-3xl font-bold text-blue-600">
                {stats.totalSessions}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Total Earnings
              </h3>
              <p className="text-3xl font-bold text-green-600">
                ${(stats.totalAmountCents / 100).toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Cancelled Sessions
              </h3>
              <p className="text-3xl font-bold text-red-600">
                {stats.cancelledSessions}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Average Per Session
              </h3>
              <p className="text-3xl font-bold text-indigo-600">
                $
                {stats.totalSessions > 0
                  ? (stats.totalAmountCents / 100 / stats.totalSessions).toFixed(2)
                  : '0.00'}
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        <SessionFilters
          filters={filters}
          onFiltersChange={setFilters}
          userType="THERAPIST"
        />

        {/* Session List */}
        {sessionData && sessionData.sessions.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {sessionData.sessions.map((session) => (
                <SessionCard
                  key={session.id}
                  session={session}
                  userType="THERAPIST"
                  onViewDetails={handleViewDetails}
                  onDownloadReceipt={handleDownloadReceipt}
                />
              ))}
            </div>

            {/* Pagination Info */}
            {sessionData.pagination.hasMore && (
              <div className="text-center">
                <button
                  onClick={() =>
                    setFilters({
                      ...filters,
                      offset: (filters.offset || 0) + (filters.limit || 20),
                    })
                  }
                  className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  Load More Sessions
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <svg
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Sessions Found
            </h3>
            <p className="text-gray-600">
              You don't have any sessions matching these filters.
            </p>
          </div>
        )}

        {/* Session Details Modal */}
        {selectedSession && !showReceipt && (
          <SessionDetails
            session={selectedSession}
            userType="THERAPIST"
            onClose={handleCloseDetails}
            onDownloadReceipt={handleDownloadReceipt}
          />
        )}

        {/* Receipt Preview Modal */}
        {selectedSession && showReceipt && (
          <ReceiptPreview
            session={selectedSession}
            onClose={handleCloseReceipt}
            onDownload={handleDownloadReceipt}
            onPrint={handlePrint}
          />
        )}
      </div>
    </div>
  );
};
